/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import * as R from 'ramda';
import StatusLed from './widgets/status-led';
import formatLastSeenOnline from '../utils/format-last-seen-online';
import { connect } from 'react-redux';

function KioskStatuses(props: any) {
  const s = props.statuses;
  if (!s) {
    return null;
  }
  return (
    <ul>
      <li>
        <StatusLed status={s.printerOnline} />
        <span>Printer {s.printerOnline === 'ok' ? 'Online' : 'Offline'}</span>
      </li>
      <li>
        <StatusLed status={s.coverClosed} />
        <span>Cover {s.coverClosed === 'ok' ? 'Closed' : 'Open'}</span>
      </li>
      <li>
        <StatusLed status={s.autoCutterOK} />
        <span>Auto Cutter {s.autoCutterOK === 'ok' ? 'OK' : 'Failure'}</span>
      </li>
      <li>
        <StatusLed status={s.headOrVoltage} />
        <span>
          {s.headOrVoltage === 'ok'
            ? 'Print Head Temperature or Voltage OK'
            : 'Print Head Temperature or Voltage Exceeded Range'}
        </span>
      </li>
      <li>
        <StatusLed status={s.paperSensorNearEnd} />
        <span>{s.paperSensorNearEnd === 'ok' ? 'Paper OK (near end check)' : 'Paper Near end'}</span>
      </li>
      <li>
        <StatusLed status={s.paperSensorNoPaper} />
        <span>{s.paperSensorNoPaper === 'ok' ? 'Paper OK' : 'No Paper'}</span>
      </li>
    </ul>
  );
}

export function KioskDeviceStatusInternal(props: any) {
  if (props.kioskType === 'hw') {
    return null;
  }
  return (
    <>
      <span title={`Kiosk status: ${props.finalStatus}, Reported: ${props.lastUpdatedOn}`}>
        <StatusLed status={props.finalStatus} redStatusName="error" greenStatusName="ok" />
      </span>
    </>
  );
}

function evalStatus(status: any, entityName: any, propName: any, propValue: any) {
  return R.pathEq(propValue, [entityName, propName])(status) ? 'ok' : 'error';
}

const emptyStatus = {
  finalStatus: 'unknown',
  finalStatusText: 'Unknown',
  lastUpdatedOn: '?',
  statuses: null
};

function mapStatuses(status: any) {
  return {
    printerOnline: evalStatus(status, 'printerStatus', 'onlineOffline', 'online'),
    coverClosed: evalStatus(status, 'offlineStatus', 'coverStatus', 'closed'),
    autoCutterOK: evalStatus(status, 'hwStatus', 'autoCutter', 'noError'),
    recoverable: evalStatus(status, 'hwStatus', 'recoverable', 'recoverable'),
    headOrVoltage: evalStatus(status, 'hwStatus', 'headOrVoltage', 'normal'),
    paperSensorNearEnd: evalStatus(status, 'paperSensorStatus', 'nearEnd', 'no'),
    paperSensorNoPaper: evalStatus(status, 'paperSensorStatus', 'noPaper', 'no')
  };
}

/**
 * Printer faults a kiosk reported, worded for the operator. Empty when the
 * kiosk sent no printer status at all (a hw kiosk behind a control box).
 */
export function printerProblems(status: any): string[] {
  if (!status?.printerStatus && !status?.paperSensorStatus) return [];
  const s = mapStatuses(status);
  const problems: string[] = [];
  if (s.printerOnline !== 'ok') problems.push('Printer offline');
  if (s.paperSensorNoPaper !== 'ok') problems.push('No paper');
  else if (s.paperSensorNearEnd !== 'ok') problems.push('Paper near end');
  if (s.coverClosed !== 'ok') problems.push('Cover open');
  if (s.autoCutterOK !== 'ok') problems.push('Cutter error');
  return problems;
}

function mapKioskStatus(status: any) {
  const statuses = mapStatuses(status);
  // Was printerOnline alone: a kiosk out of paper or with its cover open still
  // showed green, though it could not print a single ticket.
  const finalStatusOK =
    statuses.printerOnline === 'ok' && statuses.paperSensorNoPaper === 'ok' && statuses.coverClosed === 'ok'
      ? 'ok'
      : 'error';
  return {
    finalStatus: finalStatusOK,
    finalStatusText: finalStatusOK === 'ok' ? 'OK' : 'Error',
    lastUpdatedOn: formatLastSeenOnline(status.lastUpdatedOn),
    statuses
  };
}

function mapStateToProps(state: any, props: any) {
  const items = state.iotDeviceStatus.devices || [];
  const { uuid } = props;
  const item = R.find(R.propEq(uuid, 'uuid'))(items);
  if (!item || !item.status) {
    return emptyStatus;
  }
  return mapKioskStatus(item.status);
}

export const KioskDeviceStatus = connect(mapStateToProps)(KioskDeviceStatusInternal);
